import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Notes as NotesIcon } from '@mui/icons-material';
import { getFieldNotes, createFieldNote } from '../services/api';
import VoiceTranscribe from './VoiceTranscribe';
import { format, parseISO, isToday, isYesterday } from 'date-fns';

const CATEGORY_CONFIG = {
  general:  { label: 'General',  color: '#616161', bg: '#f5f5f5' },
  issue:    { label: 'Issue',    color: '#c62828', bg: '#ffebee' },
  progress: { label: 'Progress', color: '#2e7d32', bg: '#e8f5e9' },
  material: { label: 'Material', color: '#1565c0', bg: '#e3f2fd' },
  safety:   { label: 'Safety',   color: '#e65100', bg: '#fff3e0' },
};

function formatNoteDate(dateStr) {
  try {
    const d = parseISO(dateStr);
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    return format(d, 'EEEE, MMMM d, yyyy');
  } catch {
    return dateStr;
  }
}

function formatTime(ts) {
  try {
    return format(new Date(ts), 'h:mm a');
  } catch {
    return '';
  }
}

export default function FieldNotes({ projectId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Form state
  const today = new Date().toISOString().slice(0, 10);
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState('general');
  const [formDate, setFormDate] = useState(today);

  const loadNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getFieldNotes(projectId);
      setNotes(res.data);
    } catch (err) {
      setError('Failed to load field notes.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleOpenDialog = () => {
    setFormContent('');
    setFormCategory('general');
    setFormDate(today);
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formContent.trim()) {
      setSaveError('Note content is required.');
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      await createFieldNote(projectId, {
        content: formContent.trim(),
        category: formCategory,
        note_date: formDate,
      });
      setDialogOpen(false);
      await loadNotes();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save note.');
    } finally {
      setSaving(false);
    }
  };

  // Group notes by date
  const notesByDate = notes.reduce((acc, note) => {
    const key = note.note_date ? note.note_date.slice(0, 10) : 'unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(note);
    return acc;
  }, {});

  const sortedDates = Object.keys(notesByDate).sort((a, b) => b.localeCompare(a));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotesIcon fontSize="small" />
          Field Notes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Add Note
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && notes.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <NotesIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography>No field notes yet.</Typography>
          <Typography variant="body2">Tap "Add Note" to log your first note.</Typography>
        </Box>
      )}

      {/* Notes grouped by date */}
      {!loading && sortedDates.map((dateKey) => (
        <Box key={dateKey} sx={{ mb: 3 }}>
          {/* Date header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}
            >
              {formatNoteDate(dateKey)}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* Notes for this date */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {notesByDate[dateKey].map((note) => {
              const catConfig = CATEGORY_CONFIG[note.category] || CATEGORY_CONFIG.general;
              return (
                <Card
                  key={note.id}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: `4px solid ${catConfig.color}`,
                    borderRadius: 1,
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    {/* Meta row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="caption" fontWeight="bold" color="text.primary">
                        {note.author_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        · {formatTime(note.created_at)}
                      </Typography>
                      <Chip
                        label={catConfig.label}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          bgcolor: catConfig.bg,
                          color: catConfig.color,
                          ml: 'auto',
                        }}
                      />
                    </Box>
                    {/* Content */}
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}
                    >
                      {note.content}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      ))}

      {/* Add Note Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Field Note</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

          <TextField
            label="Date"
            type="date"
            fullWidth
            value={formDate}
            onChange={(e) => setFormDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ mb: 2 }}
            size="small"
          />

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={formCategory}
              label="Category"
              onChange={(e) => setFormCategory(e.target.value)}
            >
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color }} />
                    {cfg.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Note"
            multiline
            rows={5}
            fullWidth
            value={formContent}
            onChange={(e) => setFormContent(e.target.value)}
            placeholder="Describe the work, issue, or observation..."
            inputProps={{ style: { fontSize: 14 } }}
          />
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <VoiceTranscribe
              projectId={projectId}
              label="🎙️ Dictate Note"
              onTranscribed={(text) => setFormContent(prev => prev ? prev + '\n' + text : text)}
            />
            <Typography variant="caption" color="text.secondary">or type above</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !formContent.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
